'use client';

import {
  UseMutationOptions,
  useMutation,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';

import { useAuth } from '@sola/auth-sdk/store.js';
import {
  addSupporterPaymentMethod,
  removeSupporterPaymentMethod,
  saveSupporterPaymentMethod,
} from '@sola/proxy-sdk/supporters.js';
import {
  PaymentMethodTypesEnum,
  SupporterPaymentMethodDetails,
} from '@sola/schema/dotcom/supporters/paymentMethods.js';
import {
  GraphQLSupporter,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { encryptCreditCardData } from '@sola/shared-utils/credit-cards.js';

import { MOCK_SUPPORTER, MUTATION_KEYS, QUERY_KEYS } from './constants';
import { SupporterQuery } from './queries';

const useReadyForQueries = () => {
  const { authUser } = useAuth('authUser');
  return {
    isReady: !!MOCK_SUPPORTER || !!authUser?.authUserGlobalId,
    globalId: authUser?.authUserGlobalId,
  };
};

/**
 * Gets supporters main payment methods information.
 */
// export const useSupporterPaymentMethods = () => {
//   const { isReady, globalId } = useReadyForQueries();
//   return useQuery({
//     queryKey: ['supporterPaymentMethods'],
//     // If this is running, we have a global id.
//     queryFn: () => getPaymentMethods(globalId as string),
//     enabled: !!isReady,
//   });
// };

export const useSupporterQuery = (
  supporterQueries:
    | SupporterQuery<keyof GraphQLSupporter | undefined>
    | SupporterQuery<keyof GraphQLSupporter | undefined>[],
) => {
  const { isReady, globalId } = useReadyForQueries();
  const queries = Array.isArray(supporterQueries)
    ? supporterQueries
    : [supporterQueries];

  const queryResults = useQueries({
    queries: queries.map((supporterQuery) => {
      return {
        queryKey: supporterQuery.queryKey,
        // We should always have a globalId if this runs bc of isReady logic.
        queryFn: () => supporterQuery.queryFn(globalId as string),
        enabled: !!isReady,
        refetchOnWindowFocus: false,
      };
    }),
  });

  return {
    ...(queries.length === 1
      ? queryResults[0]
      : {
          data: queryResults.map((result) => result.data),
          isFetched: queryResults.every((result) => result.isFetched),
          isFetching: queryResults.some((result) => result.isFetching),
          isLoading: queryResults.some((result) => result.isLoading),
          isPending: queryResults.some((result) => result.isPending),
          isPaused: queryResults.some((result) => result.isPaused),
          isError: queryResults.some((result) => result.isError),
          isSuccess: queryResults.every((result) => result.isSuccess),
          queries: queryResults,
        }),
  };
};

export const useSupporterMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
) => {
  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    ...(!!MOCK_SUPPORTER && {
      mutationFn: async () => {
        // eslint-disable-next-line no-promise-executor-return
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return true as TData;
      },
      onSuccess: () => alert('Success!'),
    }),
  });
};

/**
 * Hook to add a new payment method.
 * @returns The result of the React Query mutation.
 */
export const usePaymentMethodAdd = () => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();

  return useSupporterMutation<
    GraphQLSupporterPaymentMethod | undefined,
    Error,
    SupporterPaymentMethodDetails
  >({
    mutationFn: (paymentMethod) => {
      const paymentMethodDetails = { ...paymentMethod };

      if (paymentMethodDetails.type === PaymentMethodTypesEnum.Card) {
        const { cardNumber, cvv } = paymentMethodDetails;
        const { creditCardEncrypted, cvvEncrypted } = encryptCreditCardData(
          { cvv, creditCard: cardNumber },
          !!cvv,
          window.TxEncrypt,
          process.env.NEXT_PUBLIC_TOKENEX_ENCRYPTION_KEY as string,
        );

        paymentMethodDetails.cardNumber = creditCardEncrypted ?? '';
        paymentMethodDetails.cvv = cvvEncrypted ?? '';
      }

      return addSupporterPaymentMethod(
        globalId as string,
        paymentMethodDetails,
      );
    },
    onSuccess: (paymentMethod?: GraphQLSupporterPaymentMethod) => {
      if (!paymentMethod?.id) {
        // TODO: Should we throw an error here?
        return;
      }

      queryClient.setQueryData(
        [QUERY_KEYS.supporterPaymentMethods],
        (paymentMethods: GraphQLSupporterPaymentMethod[]) => {
          return paymentMethods
            ? [...paymentMethods, paymentMethod]
            : [paymentMethod];
        },
      );
    },
  });
};

/**
 * Hook to update a payment method.
 * @returns The result of the React Query mutation.
 */
export const usePaymentMethodUpdate = () => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();

  return useMutation<
    GraphQLSupporterPaymentMethod,
    Error,
    GraphQLSupporterPaymentMethod
  >({
    mutationFn: (paymentMethod) =>
      saveSupporterPaymentMethod(globalId as string, paymentMethod),
    onSuccess: (updatedPaymentMethod: GraphQLSupporterPaymentMethod) => {
      queryClient.setQueryData(
        [QUERY_KEYS.supporterPaymentMethods],
        (currentPaymentMethods: GraphQLSupporterPaymentMethod[]) => {
          const newPaymentMethods = [...currentPaymentMethods];
          const paymentMethodIndex = currentPaymentMethods?.findIndex(
            (p) => p.id === updatedPaymentMethod.id,
          );
          newPaymentMethods[paymentMethodIndex] = updatedPaymentMethod;
          return newPaymentMethods;
        },
      );
    },
  });
};

/**
 * Hook to remove a payment method.
 * @returns The result of the React Query mutation.
 */
export const usePaymentMethodRemove = (paymentMethodIdKey?: string) => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();
  return useMutation({
    mutationKey: [MUTATION_KEYS.paymentMethodRemove, paymentMethodIdKey],
    mutationFn: () => {
      return removeSupporterPaymentMethod(globalId, paymentMethodIdKey);
    },
    onSuccess: async (paymentMethodId) => {
      queryClient.setQueryData(
        [QUERY_KEYS.supporterPaymentMethods],
        (paymentMethods: GraphQLSupporterPaymentMethod[]) => {
          return paymentMethods && paymentMethodId
            ? paymentMethods?.filter(
                (paymentMethod) => paymentMethod?.id !== paymentMethodId,
              )
            : [];
        },
      );
    },
  });
};
